<template>
  <div
    v-if="banner.type == 'content'"
    class="in-grid-banner content-banner col xs-span"
    :class="[
      bannerGridClass,
      `content-banner__${banner.mediaDesktop.mediaType}--desktop`,
      `content-banner__${banner.mediaMobile.mediaType}--mobile`,
      { 'horizontal-image': isHorizontal, 'vertical-image': !isHorizontal },
    ]"
    :style="getBannerStyle"
  >
    <video-player
      v-if="showVideo"
      class="content-banner__video"
      :image="isMobile? banner.mediaMobile.previewImage : banner.mediaDesktop.previewImage"
      :sources="isMobile? banner.mediaMobile.sources : banner.mediaDesktop.sources"
      :controls="{
        playPause: false,
        progressBar: false,
      }"
      :intersection-observer="{
        enable: true,
        rootMargin: '0px',
        threshold: 0.1,
      }"
    />

    <div
      class="content-banner__content"
      v-if="banner.text?.length || (banner.ctaText && banner.ctaURL)"
      :class="isMobile ? banner.alignment : banner.alignmentDesktop"
    >
      <component
        v-if="banner.text?.length"
        :is="banner.textUrl ? 'a' : 'p'"
        class="text-body-m-regular text-body-l-regular-desktop"
        :class="banner.textUrl && 'text-link'"
        :href="$localizeUrl(banner.textUrl)"
        :style="banner.textColour && `color: ${banner.textColour}`"
        v-html="banner.text"
      />

      <a
        v-if="banner.ctaText && banner.ctaURL"
        class="content-banner__btn"
        :class="[
          `button--${banner.ctaStyle} button--${banner.ctaColour}`,
          banner.ctaStyle === 'underline'
            ? 'text-body-m-regular text-body-l-regular-desktop'
            : 'text-body-2xs-regular'
        ]"
        :style="`
          background-color: ${banner.ctaColour};
          border-color: ${banner.ctaColour};
          color: ${banner.ctaTextColor}
        `"
        :href="$localizeUrl(banner.ctaURL)"
        v-text="banner.ctaText"
      />
    </div>
  </div>

  <div
    v-else-if="banner.type=='product'"
    class="in-grid-banner__product col xs-span m12 l16"
    :class="bannerGridClass"
  >
    <product-card
      :product="banner.product"
      :faceout-index="banner.faceoutIndex"
      :card-layout="specialtyTemplate ? 'large' : 'regular'"
      :carousel-limit="banner.carouselLimit"
      :image-is-linked="false"
      :show-wishlist="!specialtyTemplate"
      enable-image-carousel
      image-background-color="--color-brand-light-gray"
      :product-card-location="{
        key: 'plpHotspot',
        metaobjectName: banner.metaobjectName || null,
      }"
      show-updated-success
    />
  </div>

  <div
    v-else-if="banner.type=='hotspot'"
    class="in-grid-banner in-grid-banner__hotspot col xs-span"
    :style="getBannerStyle"
    :class="[
      `hotspot-banner__${banner.mediaDesktop.mediaType}--desktop`,
      `hotspot-banner__${banner.mediaMobile.mediaType}--mobile`,
      bannerGridClass,
      { 'horizontal-image': isHorizontal, 'vertical-image': !isHorizontal },
    ]"
  >
    <lookbook-images
      :blocks="lookbookBlock"
      :lazyload="lazyload"
      :product-card-location="{
        key: 'plpHotspot',
        metaobjectName: banner.metaobjectName || null,
      }"
    />
  </div>
</template>

<script>

/**
 * Vue: In-grid banner (in-grid-banner)
 * -----------------------------------------------------------------------------
 * In-grid banner section for collection page.
 * @param {Object} banner - Banner object.
 * @param {boolean} specialtyTemplate - PLP specialty template.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'
import LookbookImages from '~async/lookbook-images/lookbook-images'
import ProductCard from '~global/product-card/product-card'
import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'InGridBanner',

  components: {
    LookbookImages,
    ProductCard,
    ResponsiveImage,
    VideoPlayer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.video-player' */
        '~async/video-player/video-player'
      ),
    }),
  },

  props: {
    banner: {
      type: Object,
      default: () => ({
        type: null,
        alignment: null,
        alignmentDesktop: null,
        ctaColour: null,
        ctaStyle: null,
        ctaText: null,
        ctaURL: null,
        mediaDesktop: null,
        mediaMobile: null,
        positionDesktop: null,
        positionMobile: null,
        text: null,
        textColour: null,
        textUrl: null,
        width: null,
      }),
    },
    lazyload: {
      type: Boolean,
      default: false,
    },
    specialtyTemplate: {
      type: Boolean,
      default: false,
    },
  },

  computed: {

    /**
      * Map Vuex state.
      */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute banner grid class.
     * @returns {String}
     */
    bannerGridClass() {
      if (this.specialtyTemplate) {
        return 'in-grid-banner--full'
      }

      switch (this.banner.width) {
        case '2-up':
          return 'in-grid-banner--half l32'
        case '4-up':
          return 'in-grid-banner--half in-grid-banner--4up l32'
        default:
          return 'in-grid-banner--full'
      }
    },

    /**
     * Computes banner style based on existing properties
     * @returns {String}
     */
    getBannerStyle() {
      const banner = this.banner
      let bannerStyle = ''
      if (banner.type !== 'hotspot') {
        if (banner.width === '2-up' || banner.width === '4-up') {
          if (this.specialtyTemplate && banner.type === 'content' && this.isHorizontal) {
            bannerStyle += `aspect-ratio: 16 / 9;`
          }
        }
        if (banner.mediaDesktop?.mediaType === 'image') {
          bannerStyle += `--background-desktop: url(${banner.mediaDesktop.image.url});`
        }

        if (banner.mediaMobile?.mediaType === 'image') {
          bannerStyle += `--background-mobile: url(${banner.mediaMobile.image.url});`
        }
      }
      if (this.isHorizontal && banner.width === 'page-break') {
        let aspectRatio = '16 / 9'
        if (this.isMobile) {
          aspectRatio = (banner.mediaMobile.image.width / banner.mediaMobile.image.height)
            .toString()
        }
        bannerStyle += `aspect-ratio: ${aspectRatio};`
      }

      return bannerStyle
    },

    /**
     * Formats in grid banner object to lookbook block
     * @returns {Boolean}
     */
    lookbookBlock() {
      let blockImage = null
      let blockVideo = null

      if (this.isMobile) {
        if (this.banner.mediaMobile?.mediaType === 'video') {
          blockVideo = this.banner.mediaMobile
        } else {
          blockImage = this.banner.mediaMobile?.image?.url
        }
      } else if (this.banner.mediaDesktop?.mediaType === 'video') {
        blockVideo = this.banner.mediaDesktop
      } else {
        blockImage = this.banner.mediaDesktop?.image?.url
      }

      const block = [
        {
          singleItem: 'true',
          image: blockImage,
          video: blockVideo,
          products: this.banner.products,
          cardBackground: '--color-brand-light-gray',
        },
      ]

      return block
    },

    /**
     * Shows video player component
     * @returns {Boolean}
     */
    showVideo() {
      return this.isMobile
        ? this.banner.mediaMobile?.mediaType === 'video'
        : this.banner.mediaDesktop?.mediaType === 'video'
    },

    /**
     * Checks image orientation
     */
    isHorizontal() {
      if ((this.banner.type === 'content' || this.banner.type === 'hotspot') &&
        this.isMobile && this.banner.mediaMobile.media_type !== 'video') {
        return this.banner.mediaMobile?.image?.width >
          this.banner.mediaMobile?.image?.height
      }

      return this.banner.mediaDesktop?.image?.width >
        this.banner.mediaDesktop?.image?.height
    },
  },

  watch: {

    /**
     * Watch show video to load stylesheet
     * @param newShowVideo Show video
     */
    showVideo(newShowVideo) {
      if (newShowVideo) {
        this.loadStylesheet('video-player')
      }
    },
  },

  methods: {

    /**
   * Map Vuex actions.
   */
    ...mapActions({
      loadStylesheet: 'components/loadStylesheet',
    }),
  },
}
</script>

<style lang="scss">
@import './in-grid-banner';
</style>
